import React, { useEffect, useState } from 'react';
import { apiGet, apiSend } from '../api';
import { theme, card, btnPrimary, btnSecondary } from '../theme';

// TeamPanel - v0.10.0
// ----------------------------------------------------------------------
// Roster editor used both at the PROJECT level (master team list - PM,
// supervisor, builder, client contact, subs) and at the REPORT level
// (per-report distribution roster, cloned from the project team at
// report-creation time and then editable in isolation).
//
// Props:
//   kind            'project' | 'report'
//   parentId        the projectId or reportId
//   title           panel title (default depends on kind)
//   subtitle        small explainer line under the title
//
// Hits the corresponding /api/project-team or /api/report-team endpoint.
// Local state owns the row list; every save round-trips to the server,
// which is fine for these small lists (typically <20 rows).
export default function TeamPanel({ kind, parentId, title, subtitle }) {
  const isProject = kind === 'project';
  const endpoint  = isProject ? '/api/project-team' : '/api/report-team';
  const queryKey  = isProject ? 'projectId' : 'reportId';
  const bodyKey   = isProject ? 'projectId' : 'reportId';

  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({ name: '', email: '', role: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!parentId) return;
    let cancelled = false;
    setError(null);
    apiGet(`${endpoint}?${queryKey}=${encodeURIComponent(parentId)}`)
      .then(d => { if (!cancelled) setRows(Array.isArray(d) ? d : []); })
      .catch(e => { if (!cancelled) { setError(e.message); setRows([]); } });
    return () => { cancelled = true; };
  }, [parentId, endpoint, queryKey]);

  const add = async () => {
    const name = (draft.name || '').trim();
    if (!name) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await apiSend('POST', endpoint, {
        [bodyKey]: parentId,
        name,
        email: (draft.email || '').trim() || undefined,
        role: (draft.role || '').trim() || undefined,
      });
      setRows(prev => [...(prev || []), created]);
      setDraft({ name: '', email: '', role: '' });
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const update = async (id, patch) => {
    try {
      const updated = await apiSend('PATCH', `${endpoint}/${id}`, patch);
      setRows(prev => prev.map(r => r.id === id ? updated : r));
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (row) => {
    if (!confirm(`Remove ${row.name} from the team?`)) return;
    try {
      await apiSend('DELETE', `${endpoint}/${row.id}`);
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (e) {
      setError(e.message);
    }
  };

  // v0.25.0 — On-demand bulk-import of the project team into this
  // report's distribution. Only meaningful for kind='report'. Idempotent
  // server-side (dupes skipped by email/name). On success we replace
  // the local rows with the refreshed list the API returned.
  const [importing, setImporting] = useState(false);
  const [importToast, setImportToast] = useState(null);
  const importProjectTeam = async () => {
    setImporting(true); setError(null); setImportToast(null);
    try {
      const out = await apiSend('POST', endpoint, {
        [bodyKey]: parentId,
        importFromProject: true,
      });
      if (Array.isArray(out?.members)) setRows(out.members);
      const ins = out?.inserted ?? 0;
      const skp = out?.skipped ?? 0;
      const src = out?.sourceCount ?? 0;
      if (src === 0) {
        setImportToast({ kind: 'info', msg: 'The project team is empty — add members on the project, then import here.' });
      } else if (ins === 0) {
        setImportToast({ kind: 'info', msg: `All ${src} project-team members were already on this report.` });
      } else {
        setImportToast({ kind: 'ok', msg: `Imported ${ins} member${ins === 1 ? '' : 's'} from the project team${skp ? ` (${skp} skipped as already on the list)` : ''}.` });
      }
    } catch (e) {
      setError(e.message);
    } finally { setImporting(false); }
  };

  const defaultTitle = isProject ? 'Project team' : 'Report distribution';
  const defaultSubtitle = isProject
    ? 'Master roster for this project. Use it as the source for report distribution lists — each new report starts with just the owner, and you can pull this list in on demand.'
    : 'Per-report distribution list. New reports start with just the owner; click Import project team to bulk-add the rest.';

  return (
    <div style={{ ...card, padding: '14px 16px', marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.text }}>
            {title || defaultTitle}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: theme.textLight, lineHeight: 1.4 }}>
            {subtitle || defaultSubtitle}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* v0.25.0 — Import-from-project button. Report-mode only.
              Idempotent (server skips dupes by email/name). */}
          {!isProject && (
            <button
              onClick={importProjectTeam}
              disabled={importing}
              style={{ ...btnSecondary, fontSize: 12, padding: '5px 10px', opacity: importing ? 0.6 : 1 }}
              title="Bulk-add this project's team members to the report distribution list (duplicates skipped)"
            >{importing ? 'Importing…' : '+ Import project team'}</button>
          )}
          <span style={{ fontSize: 11, color: theme.textLight, fontFamily: 'monospace' }}>
            {rows ? `${rows.length} ${rows.length === 1 ? 'member' : 'members'}` : '…'}
          </span>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 8, padding: '6px 10px', background: theme.bg, color: theme.danger, border: `1px solid `, borderRadius: 6, fontSize: 12 }}>
          {error}
        </div>
      )}
      {importToast && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: theme.bg,
          color: importToast.kind === 'ok' ? theme.success : theme.text,
          border: `1px solid ${importToast.kind === 'ok' ? theme.success : theme.border}`,
          borderRadius: 6, fontSize: 12,
          display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start',
        }}>
          <span>{importToast.msg}</span>
          <button onClick={() => setImportToast(null)} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Roster table */}
      <div style={{ marginTop: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: theme.textLight, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              <th style={th}>Name</th>
              <th style={th}>Email</th>
              <th style={th}>Role</th>
              <th style={{ ...th, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={4} style={{ padding: '12px 0', color: theme.textLight, fontSize: 12 }}>Loading…</td></tr>
            )}
            {/* v0.27.0 — Empty-state row removed. The Add fields below
                are the obvious next action; the nag was just noise. */}
            {(rows || []).map(row => (
              <TeamRow key={row.id} row={row} onUpdate={update} onRemove={remove} />
            ))}
          </tbody>
        </table>
      </div>

      {/* v0.11.0 - New-member row stacks vertically on narrow viewports
          (mobile) so each field gets its own line and the Add button
          isn't squashed. */}
      <div style={{
        marginTop: 10,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 8,
      }}>
        <input
          value={draft.name}
          onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          placeholder="Name"
          style={input}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
        />
        <input
          value={draft.email}
          onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
          placeholder="Email (optional)"
          style={input}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
        />
        <input
          value={draft.role}
          onChange={e => setDraft(d => ({ ...d, role: e.target.value }))}
          placeholder="Role (e.g. PM, Supervisor, Client)"
          style={input}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
        />
        <button
          onClick={add}
          disabled={saving || !draft.name.trim()}
          style={{ ...btnPrimary, padding: '8px 14px', fontSize: 13, opacity: saving ? 0.6 : 1 }}
        >
          {saving ? 'Adding…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}

function TeamRow({ row, onUpdate, onRemove }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({ name: row.name, email: row.email || '', role: row.role || '' });
  // Sync if parent rewrites the row (e.g. after PATCH).
  useEffect(() => {
    if (!edit) setDraft({ name: row.name, email: row.email || '', role: row.role || '' });
  }, [row, edit]);

  const save = async () => {
    await onUpdate(row.id, {
      name: draft.name,
      email: draft.email || null,
      role: draft.role || null,
    });
    setEdit(false);
  };

  if (edit) {
    return (
      <tr>
        <td style={td}><input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} style={input} /></td>
        <td style={td}><input value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} style={input} /></td>
        <td style={td}><input value={draft.role} onChange={e => setDraft(d => ({ ...d, role: e.target.value }))} style={input} /></td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          <button onClick={save} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 11 }}>Save</button>
          <button onClick={() => setEdit(false)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 11, marginLeft: 4 }}>Cancel</button>
        </td>
      </tr>
    );
  }
  return (
    <tr style={{ borderTop: `1px solid ${theme.border}` }}>
      <td style={td}><strong style={{ color: theme.text }}>{row.name}</strong></td>
      <td style={{ ...td, color: theme.textLight }}>{row.email || '—'}</td>
      <td style={{ ...td, color: theme.textLight }}>{row.role || '—'}</td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        <button onClick={() => setEdit(true)} style={{ background: 'transparent', border: 'none', color: theme.brand, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Edit</button>
        <button onClick={() => onRemove(row)} style={{ background: 'transparent', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: 12, marginLeft: 6, fontFamily: 'inherit' }}>Remove</button>
      </td>
    </tr>
  );
}

const th = { padding: '6px 8px', fontWeight: 600 };
const td = { padding: '8px', verticalAlign: 'middle', fontSize: 13 };
const input = {
  width: '100%',
  padding: '7px 9px',
  border: `1px solid ${theme.border}`,
  borderRadius: 5,
  fontSize: 13,
  background: theme.surface,
  fontFamily: 'inherit',
};
